import { Badge, Stack, Table, Text } from '@mantine/core';
import {
  BRAND_KIT_COPY as C,
  TV_QUALITY_STATUS_COLORS,
  TV_QUALITY_STATUS_LABELS,
  type TvPlatformKey,
} from '@/lib/constants/appFactory';
import type { FactoryAssetQuality } from '@/lib/types/AppFactory';
import { BrandKitUtils } from '../BrandKitUtils';

interface BrandKitQualityTableProps {
  quality: FactoryAssetQuality[] | undefined | null;
  /** The TV platforms that are on for the app: a screen of one that is off is left out. */
  tv: readonly TvPlatformKey[];
}

/**
 * The quality gate, one row per SCREEN (3.42.0): how much of the screen's
 * visible area the logo fills, against that screen's floor, and the verdict —
 * Good, OK, or the reason it blocks the approval. Nothing renders for a set
 * the factory sent no quality for (an upload, an older set).
 */
export function BrandKitQualityTable({ quality, tv }: BrandKitQualityTableProps) {
  const rows = BrandKitUtils.qualityRows(quality, tv);
  if (rows.length === 0) return null;
  return (
    <Stack gap={4} data-testid="tv-quality-table">
      <Text size="sm" fw={500}>
        {C.qualityTitle}
      </Text>
      <Table verticalSpacing={4} horizontalSpacing="sm" withRowBorders={false} w="fit-content">
        <Table.Tbody>
          {rows.map((row) => (
            <Table.Tr key={row.key}>
              <Table.Td pl={0}>
                <Text size="sm">{row.label}</Text>
              </Table.Td>
              <Table.Td>
                {row.fillPct != null && (
                  <Text size="sm" c="dimmed">
                    {C.qualityFill} {Math.round(row.fillPct)}%
                    {(row.status === 'small' || row.status === 'ok') && row.minFillPct != null
                      ? ` (${C.qualityFloor.replace('{floor}', String(Math.round(row.minFillPct)))})`
                      : ''}
                  </Text>
                )}
              </Table.Td>
              <Table.Td>
                <Badge size="sm" variant="light" color={TV_QUALITY_STATUS_COLORS[row.status]}>
                  {TV_QUALITY_STATUS_LABELS[row.status]}
                </Badge>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Stack>
  );
}
